import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Loader2, AlertCircle, Volume2, VolumeX, Maximize2 } from "lucide-react";

/**
 * HLS Live player using hls.js (with native Safari fallback).
 * Designed for an RTMP→HLS pipeline (Nginx-RTMP, Ant Media, etc.).
 */
const canPlayNativeHls = () => typeof document !== "undefined" && !!document.createElement("video").canPlayType("application/vnd.apple.mpegurl");
const HLS_ERRORS = {
  [Hls.ErrorTypes.NETWORK_ERROR]: "Erreur réseau · le flux est-il en ligne ?",
  [Hls.ErrorTypes.MEDIA_ERROR]: "Erreur média · stream incompatible",
};
const hlsSupportError = () => (Hls.isSupported() || canPlayNativeHls() ? null : "HLS non supporté par ce navigateur");

export const HLSLivePlayer = ({ src, poster, channelName, channelColor }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [status, setStatus] = useState(() => ({ src, loading: true, error: hlsSupportError() }));
  const [muted, setMuted] = useState(true);
  // Per-src state: a new src resets loading/error without a synchronous setState in the effect.
  const loading = status.src === src ? status.loading : true;
  const error = status.src === src ? status.error : hlsSupportError();

  useEffect(() => {
    if (!src || !videoRef.current) return undefined;
    const video = videoRef.current;
    const ready = () => setStatus({ src, loading: false, error: null });
    const fail = (msg) => setStatus({ src, loading: false, error: msg });

    // Native HLS support (Safari, iOS)
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      video.addEventListener("loadedmetadata", ready, { once: true });
      video.addEventListener("error", () => fail("Flux HLS injoignable"), { once: true });
      return () => { video.src = ""; };
    }

    // hls.js fallback (unsupported browsers already carry hlsSupportError())
    if (!Hls.isSupported()) return undefined;
    const hls = new Hls({ liveDurationInfinity: true, lowLatencyMode: true });
    hlsRef.current = hls;
    hls.loadSource(src);
    hls.attachMedia(video);
    hls.on(Hls.Events.MANIFEST_PARSED, ready);
    hls.on(Hls.Events.ERROR, (_e, data) => {
      if (!data.fatal) return;
      fail(HLS_ERRORS[data.type] || "Flux indisponible");
    });

    return () => {
      hls.destroy();
      hlsRef.current = null;
    };
  }, [src]);

  const requestFullscreen = () => {
    if (videoRef.current?.requestFullscreen) videoRef.current.requestFullscreen();
    else if (videoRef.current?.webkitEnterFullscreen) videoRef.current.webkitEnterFullscreen();
  };

  return (
    <div className="relative aspect-video w-full bg-black rounded-xl overflow-hidden" data-testid="hls-live-player">
      <video
        ref={videoRef}
        autoPlay
        muted={muted}
        playsInline
        controls
        poster={poster}
        className="w-full h-full"
        data-testid="hls-video"
      />

      {/* LIVE badge overlay */}
      <div className="absolute top-3 left-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500 text-white text-[10px] uppercase tracking-[0.3em] font-bold shadow-lg pointer-events-none">
        <span className="relative inline-flex w-2 h-2">
          <span className="absolute inset-0 rounded-full bg-white animate-ping" />
          <span className="relative rounded-full bg-white w-2 h-2" />
        </span>
        En direct
      </div>

      {/* Channel pill */}
      {channelName && (
        <div className="absolute top-3 right-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-md text-white text-[10px] uppercase tracking-[0.3em] pointer-events-none">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: channelColor || "#fff" }} />
          {channelName}
        </div>
      )}

      {/* Quick controls (top-right secondary) */}
      <div className="absolute bottom-3 right-3 flex items-center gap-2 z-10">
        <button
          onClick={() => { setMuted(m => !m); if (videoRef.current) videoRef.current.muted = !muted; }}
          aria-label={muted ? "Activer le son" : "Couper le son"}
          data-testid="hls-mute"
          className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-black/70 backdrop-blur-md text-white hover:bg-[color:var(--accent)] hover:text-black transition"
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
        <button
          onClick={requestFullscreen}
          aria-label="Plein écran"
          data-testid="hls-fullscreen"
          className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-black/70 backdrop-blur-md text-white hover:bg-[color:var(--accent)] hover:text-black transition"
        >
          <Maximize2 className="w-4 h-4" />
        </button>
      </div>

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60">
          <Loader2 className="w-8 h-8 animate-spin text-[color:var(--accent)]" />
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 px-6 text-center">
          <AlertCircle className="w-8 h-8 text-red-400 mb-2" />
          <p className="text-sm text-white" data-testid="hls-error">{error}</p>
          <p className="text-[10px] text-[color:var(--text-muted)] uppercase tracking-widest mt-2">
            Vérifiez que votre serveur RTMP est actif et accessible
          </p>
        </div>
      )}
    </div>
  );
};

export default HLSLivePlayer;
